import axios from 'axios'
import { httpsCallable } from 'firebase/functions'
import { AppContext } from 'global/cnr/contexts/AppProvider'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { ammendMedia_alignment } from './mediaHelpers/sportAlignments'
import { ammendMedia_results } from './mediaHelpers/sportsResults'
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext'
import { sportsHelpers } from '../cnr/reducers/reducerHelpers/sportsHelpers'

export const useFetchMedia = ({
  concurrency = 4,
  delay = 750
} = {}) => {

  const appContext = useContext(AppContext);
  const { app_state } = appContext
  const { mainFunctions, app_alts, app_prods, mainStorage } = app_state

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const {
    currentSeason_info,
    links_score,
    teams_info,
  } = sportsSeason_state ?? {}

  const { latestSeason, _sportsAbr, _sportsModeKey, _sportsModeSport } = currentSeason_info ?? {}

  const currentSeason = useMemo(() => {
    let _season = 'fall'
    switch (_sportsModeSport) {
      case 'soccer':
      case 'football':
        _season = 'fall'
        break;
      case 'basketball':
        _season = 'winter'
        break;
      case 'baseball':
      case 'softball':
        _season = 'spring'
        break;
      default:
    }
    return _season
  }, [_sportsModeSport])

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // avoids race conditions if called twice
  const runIdRef = useRef(0)

  const getMedia_resultsOnly = useCallback(async (callData, withFunctions) => {


    const runId = ++runIdRef.current

    setLoading(true)
    setError(null)

    try {
      const {
        useSingleDate,
        singleDate,
        year,
      } = callData

      const _year = year % 100

      let { mediaUrl, mediaUrlDateReplace, mediaUrlYearReplace } = links_score ?? {}
      let _url = mediaUrl && mediaUrlYearReplace ? mediaUrl.replace(mediaUrlYearReplace, _year) : mediaUrl.replace('25', _year)
      const _mediaUrlDateReplace = mediaUrlDateReplace || 'TODAYSDATE'

      const _dateGroupKeys = sportsHelpers.generateDatesByYear([year], currentSeason)
      const _dateGroupKeys_current = useSingleDate && singleDate ? [singleDate] : _dateGroupKeys[year]

      if (!_dateGroupKeys_current?.length) {
        setData([])
        return []
      }

      if (withFunctions) {
        const getMediaScores = httpsCallable(mainFunctions, 'getMediaScores');
        const response = await getMediaScores(callData)
        console.log('response', response)
      }

      const mergedMatches = await runWithConcurrency(
        _dateGroupKeys_current,
        async date => {
          const _date = formatDate(date)
          const _url_date = _mediaUrlDateReplace ? _url.replace(_mediaUrlDateReplace, _date) : _url
          const html = await fetchWithRetry(_url_date,)
          return ammendMedia_results(callData, _date, html)
        },
        { concurrency, delay }
      )

      // ignore stale runs
      if (runId === runIdRef.current) {
        setData(mergedMatches)
      }

      return mergedMatches
      // console.log('mergedMatches', mergedMatches)
      // callback({ results: mergedMatches })

    } catch (err) {
      if (runId === runIdRef.current) {
        setError(err)
      }
      throw err
    } finally {
      if (runId === runIdRef.current) {
        setLoading(false)
      }
    }
  }, [concurrency, delay])

  const getMedia_standingsOnly = useCallback(async (callData) => {
    const runId = ++runIdRef.current
    setLoading(true)
    setError(null)

    try {
      const { links_score, year, existingTeams, database_fns } = callData
      let { mediaUrl } = links_score ?? {}
      const _year = year % 100
      const { mediaUrlDateReplace, mediaUrlYearReplace } = links_score ?? {}
      let _url = mediaUrl && mediaUrlDateReplace ? mediaUrl.replace('schedule/' + mediaUrlDateReplace, 'standings') : null
      _url = mediaUrl && mediaUrlYearReplace ? _url.replace(mediaUrlYearReplace, _year) : _url.replace('25', _year)

      console.log('_url', _url)
      const html = await fetchWithRetry(_url)
      // const doc = parser.parseFromString(result, 'text/html')
      const results = ammendMedia_alignment(html, existingTeams, _url, database_fns)
      // ignore stale runs
      if (runId === runIdRef.current) {
        setData(results)
      }

      console.log('results', results)
      return results
      // console.log('mergedMatches', mergedMatches)
      // callback({ results: mergedMatches })

    } catch (err) {
      if (runId === runIdRef.current) {
        setError(err)
      }
      throw err
    } finally {
      if (runId === runIdRef.current) {
        setLoading(false)
      }
    }
  }, [concurrency, delay])

  return {
    getMedia_resultsOnly,
    getMedia_standingsOnly,
    data,
    loading,
    error
  }
}

const sleep = ms => new Promise(r => setTimeout(r, ms))

const axiosInstance = axios.create({
  method: 'get',
  timeout: 30000,
  headers: {
    'Accept': 'text/html,application/xhtml+xml'
    // 'User-Agent': 'Mozilla/5.0',
  }
})

const fetchWithRetry = async (url, retries = 2) => {
  try {
    const res = await axiosInstance.get(url)
    return res.data
  } catch (err) {
    if (retries > 0 && err.code === 'ECONNABORTED') {
      await sleep(1000 * (3 - retries))
      return fetchWithRetry(url, retries - 1)
    }
    throw err
  }
}

const runWithConcurrency = async (
  items,
  handler,
  { concurrency = 4, delay = 750 } = {}
) => {
  const results = []
  const queue = [...items]

  const workers = Array.from({ length: concurrency }, async () => {
    while (queue.length) {
      const item = queue.shift()
      try {
        const res = await handler(item)
        results.push(res)
      } catch (err) {
        console.log('worker error', err.message)
      }
      await sleep(delay)
    }
  })

  await Promise.all(workers)
  return results.flat()
}

// const parseTheResultXXX = (callData, date, result) => {
//   if (!result) return []

//   const {
//     _useSingleDate,
//     singleDate,
//     singleDates,
//   } = callData

//   const doc = new DOMParser().parseFromString(result, 'text/html')
//   const table = doc.querySelector('table.schedule')

//   if (!table) return []

//   const trs = table.querySelectorAll('tr')
//   const matchData = []

//   // 🔑 explicit state — no DOM guessing
//   // let currentLevel = null
//   // let currentSection = null
//   // let currentSpecial = null

//   trs.forEach(tr => {

//     const matchAttributes = {
//       level: null,
//       section: null,
//       special: null
//     }

//     // header rows
//     if (tr.classList.contains('class-name')) {
//       matchAttributes.level = tr.textContent.trim()
//       // currentLevel = tr.textContent.trim()
//       // currentSection = null
//       // currentSpecial = null
//       // Class 6A
//       return
//     }

//     if (tr.classList.contains('conference-name')) {
//       // currentSection = tr.textContent.trim()
//       matchAttributes.section = tr.textContent.trim()
//       // Non Conference
//       // Tournaments
//       // Section 1 (1A)
//       // Non Conference
//       return
//     }

//     if (tr.classList.contains('special-game')) {
//       matchAttributes.special = tr.textContent.trim()
//       // currentSpecial = tr.textContent.trim()
//       // Tournament: Shootout at St. Vincent College
//       return
//     }

//     const tds = tr.querySelectorAll('td')
//     if (tds.length !== 3) return

//     // const _header = {
//     //   ...(currentLevel && { level: currentLevel }),
//     //   ...(currentSection && { section: currentSection }),
//     //   ...(currentSpecial && { special: currentSpecial })
//     // }

//     const m = getMediaMatch(tds, date, matchAttributes)

//     if (_useSingleDate && m?.startDate === singleDate) {
//       console.log('match', m)
//     }

//     if (_useSingleDate && !_.isEmpty(singleDates) && _.includes(singleDates, m?.startDate)) {
//       console.log('match', m)
//     }

//     if (m) {
//       matchData.push(m)
//     }
//   })

//   return matchData
// }

// const getMediaMatch = (tds, date, matchAttributes) => {

//   const _lsp = {
//     _matchCollectionKey: _mediaHeaders.tbd,
//     level: _mediaHeaders.tbd,
//     section: _mediaHeaders.tbd,
//     playoff: null,
//     playoffBracketType: null,
//     _exclude: false
//   }

//   const m = { startDate: date, _header: matchAttributes }

//   //   const matchAttributes = {
//   //   level: null,
//   //   section: null,
//   //   special: null
//   // }

//   const { level, section, special } = matchAttributes

//   if (special) {

//     // level   
//     // Class 6A

//     // section
//     // Non Conference
//     // Tournaments
//     // Section 1 (1A)
//     // Non Conference

//     // special 
//     // Tournament: Shootout at St. Vincent College

//     const isTournament = special && special.includes('Tournament') && section && section.includes('Tournament')

//     if (isTournament) {
//       _lsp._matchCollectionKey = _mediaHeaders.tournament
//       _lsp.level = _mediaHeaders.tournament
//       _lsp.section = _mediaHeaders.tournament
//     } else {
//       const { sportLevel, isWpial, playoffBracketType } = getPlayoffLevelFromText(special)
//       if (isWpial && sportLevel) {
//         _lsp.level = sportLevel
//         _lsp.section = _mediaHeaders.nonSection
//         _lsp.playoffBracketType = playoffBracketType
//         _lsp.playoff = playoffBracketType ? true : false
//       } else {
//         _lsp._exclude = true
//       }
//     }
//   }

//   if (level && !special) {
//     switch (level) {
//       case 'Playoffs':
//         _lsp.playoff = true
//         break;
//       default:
//         _lsp.level = getLevelFromText(level)
//       // nothing
//     }
//   }

//   if (section && !_lsp.section) {
//     switch (section) {
//       case 'Non Conference':
//         _lsp.level = _mediaHeaders.tbd
//         _lsp.section = _mediaHeaders.nonSection
//         break;
//       case 'Tournament':
//       case 'Tournaments':
//         // nothing - already set above
//         break;
//       default:
//         _lsp.section = section.replace('(' + _lsp.level + ')', '').trim()
//     }
//   }

//   m._lsp = _lsp

//   const teamSplit = _lsp.playoff ? ' vs ' : ' at '

//   const startTime = tds[0] ? tds[0].textContent : '7:00 PM'
//   const _teams = tds[1] ? tds[1].textContent : null
//   const _teams_s = _teams.split(teamSplit)

//   let homeName
//   let awayName;
//   let locationName;

//   if (_teams_s.length === 2) {
//     awayName = _teams_s[0]
//     let _home = _teams_s[1]
//     if (_lsp.playoff) {
//       const homeSplit = _home.split(' at ')
//       homeName = homeSplit[0]
//       locationName = homeSplit[1]
//     } else {
//       homeName = _teams_s[1]
//     }

//     homeName = ammendTeamName(homeName)
//     awayName = ammendTeamName(awayName)

//     m.away = awayName
//     m.home = homeName

//     m._matchCollectionKey = _lsp._matchCollectionKey
//     m.sections = _lsp.section
//     m.levels = _lsp.level

//     m.startTime = startTime

//     if (_lsp.playoff) {
//       m.isPlayoff = true
//       m.roundKey = 'playoffs'
//       if (_lsp.playoffBracketType) {
//         m.poolKey = _lsp.playoffBracketType
//       }
//     }

//     // ammendMatchLevelAndSectionByMatchTeams(m, teams, awayName, homeName, locationName)
//     const _result = tds[1] ? tds[2].textContent.trim() : null

//     if (_result) {
//       if (_result === 'PPD') {
//         return null
//       } else {
//         ammendResults(m, _result)
//       }
//     }
//   }

//   if (m._lsp.level !== m.levels) {
//     console.log('mismatch', m)
//   }

//   return _lsp._exclude ? null : m
// }

// const ammendResults = (match, result) => {

//   const _result_s = result.split('-')
//   const _result_ot_s = result.split(' ')

//   if (_result_s.length === 2) {
//     match.awayScore = _result_s[0].trim()
//     match.homeScore = _result_s[1].trim()
//     if (_result_ot_s.length === 2 && _result_ot_s[1]) {
//       match.extra = _result_ot_s[1]
//       match.homeScore = match.homeScore.replace(match.extra, '')
//       match.homeScore = match.homeScore.trim()
//     }
//   }
// }

// const getLevelFromText = (text) => {
//   let sportLevel;
//   try {
//     _.forEach(_sportLevels, (sl) => {
//       if (text.includes(sl)) {
//         sportLevel = sl
//       }
//     })
//   } catch (error) {
//     console.log('getLevelFromText error', text)
//   }
//   return sportLevel
// }

// const getPlayoffLevelFromText = (text) => {

//   let sportLevel;
//   let isWpial;
//   let playoffBracketType;
//   try {

//     isWpial = text.indexOf('WPIAL') >= 0

//     _.forEach(_sportLevels, (sl) => {
//       if (text.indexOf(sl) >= 0) {
//         sportLevel = sl
//       }
//     })

//     _.forEach(playoffBracketTypes, (pbt) => {
//       const _text = text.toLowerCase()
//       if (_text.includes(pbt.toLowerCase())) {
//         playoffBracketType = pbt
//       }
//       if (!playoffBracketType && _text.includes('first round')) {
//         playoffBracketType = playoffBracketTypes.roundOf16
//       }
//       if (!playoffBracketType && _text.includes('championship')) {
//         playoffBracketType = playoffBracketTypes.finals
//       }
//       if (!playoffBracketType && _text.includes('consolation')) {
//         playoffBracketType = playoffBracketTypes.consolation
//       }
//       if (!playoffBracketType && _text.includes('preliminary round')) {
//         // playoffBracketType = playoffBracketTypes.preliminaryRound
//         playoffBracketType = playoffBracketTypes.roundOf32
//       }
//       if (!playoffBracketType && _text.includes('first round')) {
//         // playoffBracketType = playoffBracketTypes.preliminaryRound
//         playoffBracketType = playoffBracketTypes.roundOf16
//       }
//     })
//   } catch (error) {
//     console.log('getLevelFromText error', text)
//   }

//   if (!playoffBracketType) {
//     console.log('text', text)
//   }
//   return { sportLevel, isWpial, playoffBracketType }
// }

function formatDate(inputDate) {
  // Split the input date into components
  var dateComponents = inputDate.split('/');

  // Extract month, day, and year
  var month = dateComponents[0];
  var day = dateComponents[1];
  var year = dateComponents[2];

  // Create a new date in 'yyyy-mm-dd' format
  var formattedDate = year + '-' + month + '-' + day;

  return formattedDate;
}

// function createAlignmentsFromMediaXXX(result, existingTeams, _url, database_fns) {

//   if (!result) return []

//   const doc = new DOMParser().parseFromString(result, 'text/html')

//   const alignments = {};
//   const teamRecords = {}
//   const teams = {}
//   const nonTeams = []
//   let sportsYearStandings;

//   doc.querySelectorAll('.sport').forEach((sport) => {
//     const _standings = sport.querySelectorAll('.team-top')
//     if (_standings) {
//       const header = sport.querySelectorAll('h2')
//       try {
//         if (header && header[0]) {
//           sportsYearStandings = header[0].textContent.split(' ')[0]
//           sportsYearStandings = sportsYearStandings.split('-')[1]
//         }
//       } catch (error) {
//         console.log('error', error)
//       }
//     }
//     sport.querySelectorAll('.standings').forEach((standingsDiv) => {
//       standingsDiv.querySelectorAll('table').forEach((table) => {
//         let _district = getClosestDiv(table, 'district-name');
//         switch (_district) {
//           case 'WPIAL':
//             let _level = getClosestDiv(table, 'classification-name');
//             let _section = getClosestDiv(table, 'conference-name');
//             if (_level && _section) {

//               _level = _level.replace('Class ', '')
//               _section = _section.replace('(' + _level + ')', '')
//               _section = _.camelCase(_section)

//               table.querySelectorAll('tbody').forEach((tbody) => {
//                 tbody.querySelectorAll('tr').forEach((tr) => {

//                   let team;
//                   let _itemKey;

//                   tr.querySelectorAll('td').forEach((td, index) => {
//                     switch (index) {
//                       case 0:
//                         team = td.textContent.trim()
//                         team = ammendTeamName(team)

//                         const _team = _.find(existingTeams, { name: team })
//                         _itemKey = _team ? _team._itemKey : database_fns.get_docKey()

//                         teamRecords[_itemKey] = {
//                           name: team,
//                           [sportsYearStandings]: {
//                             record: '',
//                             overallRecord: '',
//                             section: _section,
//                             level: convertHelpers.compactText(_level)
//                           }
//                         }

//                         if (!alignments[_level]) { alignments[_level] = { sections: {} } }
//                         if (!alignments[_level].sections[_section]) { alignments[_level].sections[_section] = { teams: {} } }
//                         if (_team) {
//                           alignments[_level].sections[_section].teams[_team.name] = {
//                             name: _team.name,
//                             _itemKey: _itemKey,
//                             exists: true
//                           }
//                         } else {
//                           alignments[_level].sections[_section].teams[team] = {
//                             name: team,
//                             _itemKey: _itemKey
//                           }
//                           nonTeams.push(team)
//                         }
//                         break;
//                       case 1:
//                         const r = td.textContent.trim()
//                         teamRecords[_itemKey][sportsYearStandings].record = r
//                         break;
//                       case 3:
//                         const ro = td.textContent.trim()
//                         teamRecords[_itemKey][sportsYearStandings].overallRecord = ro
//                         break;
//                       default:
//                     }

//                   })
//                 })
//               })
//             }
//             break;
//           default:
//           // nothing
//         }
//       })
//     })
//   })

//   _.forEach(alignments, (alignment, levelKey) => {
//     const { sections } = alignment
//     _.forEach(sections, (section, sectionKey) => {
//       const { teams: teams_section } = section
//       let position = 0
//       _.forEach(teams_section, (team, teamKey) => {
//         const { name, _itemKey } = team
//         teams[_itemKey] = {
//           name,
//           sections: sectionKey,
//           levels: levelKey,
//           position
//         }
//         position++
//       })
//     })
//   })

//   const sectionCounts = getSectionCountsFromTeams(teams)

//   return { sportsYearStandings, alignments, sectionCounts, teams, teamRecords, nonTeams, _url }
// }

// const getSectionCountsFromTeams = (teams) => {
//   // Group by level and count unique sections starting with 'section' for each level
//   return _(teams)
//     .groupBy('levels')
//     .mapValues(levelTeams =>
//       _(levelTeams)
//         // .filter(team => team.sections.startsWith('section')) // Include only sections that start with 'section'
//         .uniqBy('sections')
//         .size()
//     )
//     .value();
// };

// function getClosestDiv(tableElement, selector) {
//   let currentElement = tableElement.previousElementSibling;

//   while (currentElement) {
//     // Check if the element is a `div` with the class 'classification-name'
//     if (currentElement.classList.contains(selector)) {
//       return currentElement.textContent.trim(); // Return the text content if found
//     }
//     currentElement = currentElement.previousElementSibling;
//   }

//   return null; // Return null if no 'classification-name' div is found
// }
